/**
 * What a tour step spotlights. Targets match `data-tour-section` attributes on
 * the Meeting View's landmarks, the same convention the home QuickTour uses,
 * extended with meeting-prefixed keys so the two sets can't collide.
 */
export type MeetingTourTarget =
  | 'meeting-header'
  | 'meeting-tabs'
  | 'meeting-follow-ups'
  | 'meeting-summary'
  | 'meeting-notepad-toggle'
  | 'meeting-chat'
  | 'meeting-actions'

export interface MeetingTourStepDescriptor {
  target: MeetingTourTarget
  title: string
  description: string
  side: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
}

/**
 * The full tour, top of the view to the footer. Callers filter this against
 * the DOM at open time: a meeting with no follow-ups renders no For You
 * section, so its step has nothing to point at, and the chat step drops out
 * while a notepad is holding the footer slot. The recap steps assume the
 * Recap tab, which is where a finished meeting opens (HIG-6354).
 */
export const buildMeetingTourSteps = (): Array<MeetingTourStepDescriptor> => [
  {
    target: 'meeting-header',
    title: 'Your meeting, captured',
    description:
      'Every meeting you record lands here: title, time, and who was in the room, with the full record below.',
    side: 'bottom',
  },
  {
    target: 'meeting-tabs',
    title: 'Brief, transcript, recap',
    description:
      'Switch between the brief that prepared you, everything that was said, and the recap below: what came out of it.',
    side: 'bottom',
  },
  {
    target: 'meeting-follow-ups',
    title: 'Follow-ups, drafted for you',
    description: 'Highlight pulls the commitments out of the conversation and drafts the next step for each one.',
    side: 'bottom',
  },
  {
    target: 'meeting-summary',
    title: 'The summary',
    description: 'A readable recap of what happened. Skim it before your next meeting, or copy it to share.',
    side: 'top',
  },
  {
    target: 'meeting-notepad-toggle',
    title: 'Your notepad',
    description: 'Take your own notes during any meeting. This button swaps the chat for your notepad and back.',
    side: 'top',
  },
  {
    target: 'meeting-chat',
    title: 'Ask about this meeting',
    description:
      'Ask Highlight anything about what was said, and it answers from this meeting and the rest of your work.',
    side: 'top',
  },
  {
    target: 'meeting-actions',
    title: 'Act on it',
    description: 'Copy the summary, share a link to this meeting, or delete it, all from here.',
    side: 'top',
    align: 'end',
  },
]
